import { defineStore } from 'pinia'

export interface SsoUser {
  id: number
  email: string
  name: string
  first_name: string
  last_name: string
  avatar_url: string | null
  role: string
  global_role: string
  groups: { id: number, name: string }[]
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<SsoUser | null>(null)
  const loading = ref(true)
  const loggingOut = ref(false)
  const authenticated = computed(() => !!user.value)

  /**
   * Vrai quand la derniere tentative a echoue pour une raison autre qu'une
   * absence de session : SSO injoignable, 500, coupure reseau. La distinction
   * compte pour l'appelant — une session absente se resout en renvoyant vers
   * le SSO, une panne ne se resout pas ainsi et ne doit pas etre presentee
   * comme un refus de droits.
   */
  const unreachable = ref(false)

  /**
   * Requete en cours, partagee entre appelants simultanes.
   *
   * Un garde temporel remplacait ceci : deux appels a moins de deux secondes
   * d'intervalle, le second retournait immediatement sans rien charger. Le
   * second appelant repartait alors avec `user` a `null` alors qu'une requete
   * etait en vol — au retour du callback SSO, ou le plugin de rafraichissement
   * et le middleware se declenchent dans le meme instant, cela produisait un
   * ecran d'acces refuse sur une session pourtant valide. Partager la promesse
   * dedoublonne sans jamais rendre la main sur un etat vide.
   */
  let inFlight: Promise<void> | null = null

  async function fetchSession() {
    if (inFlight) return inFlight
    inFlight = doFetchSession().finally(() => { inFlight = null })
    return inFlight
  }

  async function doFetchSession() {
    loading.value = true
    try {
      // En SSR, `/api/auth/session` est appelé côté serveur : sans relais
      // explicite, les en-têtes de la requête de navigation sont perdus. Les
      // en-têtes de simulation de rôle (voir `server/utils/auth.ts`) doivent
      // suivre le cookie, faute de quoi le rendu serveur verrait le rôle réel
      // là où l'API voit le rôle simulé — et les tests de permissions côté
      // pages ne prouveraient rien.
      const headers = import.meta.server
        ? useRequestHeaders(['cookie', 'x-test-role', 'x-test-global-role'])
        : undefined
      const res = await $fetch<{ success: boolean, data: { user: SsoUser } }>('/api/auth/session', { headers })
      user.value = res.data.user
      unreachable.value = false
    }
    catch (err) {
      // Un 401 est la réponse normale d'un visiteur sans session : la route la
      // renvoie dès qu'aucun jeton valide n'a pu être obtenu. Toute autre
      // erreur — panne réseau, 500 — mène au même écran de déconnexion, mais
      // pour une raison qui, elle, mérite une trace : sans elle, une
      // indisponibilité du SSO se lit comme une simple fin de session.
      const status = (err as { response?: { status?: number } }).response?.status
      if (status !== 401) {
        console.error('[auth] Session illisible pour une raison autre qu\'une absence de session', err)
      }
      unreachable.value = status !== 401
      user.value = null
    }
    finally {
      loading.value = false
    }
  }

  async function logout() {
    loggingOut.value = true
    const config = useRuntimeConfig()
    if (import.meta.client) {
      try {
        await $fetch('/api/auth/logout', { method: 'POST' })
      }
      catch {
        // Ignorer
      }
      window.location.href = `${config.public.ssoUrl}/api/auth/logout`
    }
  }

  return {
    user,
    loading,
    loggingOut,
    authenticated,
    unreachable,
    fetchSession,
    logout,
  }
})
